import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, Settings, Save, Play, Mic, Volume2, 
  CheckCircle, AlertCircle, Loader2, PhoneCall,
  User, FileText, Globe, Zap, Bot, Headphones,
  DollarSign, Clock, TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useBusinessContent } from '../contexts/BusinessContentContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Dark Theme Colors
const THEME = {
  bg: '#0f1117',
  cardBg: '#1a2332',
  cardBorder: '#1e2d3d',
  accent: '#00d4aa',
  textPrimary: '#ffffff',
  textSecondary: '#94a3b8',
  warning: '#f59e0b',
  warningBg: '#1a1a00',
  inputBg: '#0f1117'
};

const VoiceAISettings = () => {
  const { getCallScript, getBusinessTypeName } = useBusinessContent();
  const callScript = getCallScript();
  
  const [settings, setSettings] = useState({
    agent_name: 'Aria',
    default_script: 'general',
    language: 'english'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [scripts, setScripts] = useState({});
  const [isConfigured, setIsConfigured] = useState(true);
  const [stats, setStats] = useState({
    credits_remaining: 9.28,
    calls_today: 0,
    calls_this_month: 0
  });

  useEffect(() => {
    fetchSettings();
    fetchScripts();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/vapi/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSettings({
        agent_name: data.agent_name || 'Aria',
        default_script: data.default_script || 'general',
        language: data.language || 'english'
      });
      setIsConfigured(data.is_configured !== false);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchScripts = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/vapi/scripts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setScripts(data.scripts || {});
    } catch (error) {
      console.error('Error fetching scripts:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/vapi/settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      if (res.ok) {
        toast.success('Settings saved successfully');
        setIsConfigured(true);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestCall = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number');
      return;
    }

    setTesting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/vapi/test-call`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phone_number: testPhone })
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success('Test call initiated! You should receive a call shortly.');
      } else {
        toast.error(data.detail || 'Failed to initiate test call');
      }
    } catch (error) {
      toast.error('Failed to initiate test call');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div 
        className="flex items-center justify-center h-64"
        style={{ backgroundColor: THEME.bg }}
      >
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: THEME.accent }} />
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen p-6 space-y-6"
      style={{ backgroundColor: THEME.bg }}
      data-testid="voice-ai-settings"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${THEME.accent}20` }}
          >
            <Bot className="w-6 h-6" style={{ color: THEME.accent }} />
          </div>
          <div>
            <h2 className="text-2xl font-bold" style={{ color: THEME.textPrimary }}>
              {getBusinessTypeName()} Voice AI
            </h2>
            <p style={{ color: THEME.textSecondary }}>
              Configure your AI calling assistant
            </p>
          </div>
        </div>
        <Badge 
          className="px-3 py-1.5"
          style={{ backgroundColor: `${THEME.accent}20`, color: THEME.accent, border: `1px solid ${THEME.accent}30` }}
        >
          <CheckCircle className="w-4 h-4 mr-1" />
          Connected
        </Badge>
      </div>

      {/* Connection Status Card */}
      <div className="grid md:grid-cols-3 gap-4">
        <div 
          className="md:col-span-2 rounded-xl p-6"
          style={{ backgroundColor: THEME.cardBg, border: `1px solid ${THEME.cardBorder}` }}
        >
          <h3 className="text-lg font-semibold mb-4" style={{ color: THEME.textPrimary }}>
            Voice AI Integration
          </h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5" style={{ color: THEME.accent }} />
              <span style={{ color: THEME.textSecondary }}>
                Status: <span style={{ color: THEME.accent }} className="font-medium">Connected</span>
              </span>
            </div>
            <div className="flex items-center gap-3">
              <User className="w-5 h-5" style={{ color: THEME.accent }} />
              <span style={{ color: THEME.textSecondary }}>
                Assistant: <span style={{ color: THEME.textPrimary }} className="font-medium">{settings.agent_name}</span>
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5" style={{ color: THEME.accent }} />
              <span style={{ color: THEME.textSecondary }}>
                Language: <span style={{ color: THEME.textPrimary }} className="font-medium">Hindi/English</span>
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5" style={{ color: THEME.accent }} />
              <span style={{ color: THEME.textSecondary }}>
                Number: <span style={{ color: THEME.textPrimary }} className="font-medium">+1 870 454 3901</span>
              </span>
            </div>
          </div>
        </div>

        {/* Credits Card */}
        <div 
          className="rounded-xl p-6"
          style={{ backgroundColor: THEME.cardBg, border: `1px solid ${THEME.accent}30` }}
        >
          <p className="text-sm mb-2" style={{ color: THEME.textSecondary }}>Credits remaining</p>
          <p className="text-4xl font-bold" style={{ color: THEME.accent }}>${stats.credits_remaining}</p>
          <div className="mt-4 space-y-1">
            <p className="text-sm" style={{ color: THEME.textSecondary }}>
              Calls today: {stats.calls_today}
            </p>
            <p className="text-sm" style={{ color: THEME.textSecondary }}>
              This month: {stats.calls_this_month}
            </p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Agent Settings Card */}
        <div 
          className="rounded-xl p-6"
          style={{ backgroundColor: THEME.cardBg, border: `1px solid ${THEME.cardBorder}` }}
        >
          <h3 className="text-lg font-semibold mb-6 flex items-center gap-2" style={{ color: THEME.textPrimary }}>
            <Headphones className="w-5 h-5" style={{ color: THEME.accent }} />
            AI Agent Settings
          </h3>
          
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.textSecondary }}>
                Agent Name
              </label>
              <input
                type="text"
                value={settings.agent_name}
                onChange={(e) => setSettings({...settings, agent_name: e.target.value})}
                placeholder="Enter agent name (e.g., Aria)"
                className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2"
                style={{ 
                  backgroundColor: THEME.inputBg, 
                  border: `1px solid ${THEME.cardBorder}`,
                  color: THEME.textPrimary
                }}
              />
              <p className="text-xs mt-1" style={{ color: THEME.textSecondary }}>
                This is how the AI will introduce itself on calls
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.textSecondary }}>
                Default Script
              </label>
              <select
                value={settings.default_script}
                onChange={(e) => setSettings({...settings, default_script: e.target.value})}
                className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2"
                style={{ 
                  backgroundColor: THEME.inputBg, 
                  border: `1px solid ${THEME.cardBorder}`,
                  color: THEME.textPrimary
                }}
              >
                {Object.entries(scripts).map(([key, script]) => (
                  <option key={key} value={key} style={{ backgroundColor: THEME.cardBg }}>
                    {script.name}
                  </option>
                ))}
                {Object.keys(scripts).length === 0 && (
                  <option value="general" style={{ backgroundColor: THEME.cardBg }}>General Script</option>
                )}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.textSecondary }}>
                Language
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setSettings({...settings, language: 'english'})}
                  className="flex-1 px-4 py-3 rounded-lg font-medium transition-all"
                  style={{
                    backgroundColor: settings.language === 'english' ? THEME.accent : THEME.cardBorder,
                    color: settings.language === 'english' ? '#000000' : THEME.textSecondary
                  }}
                >
                  English
                </button>
                <button
                  type="button"
                  onClick={() => setSettings({...settings, language: 'hindi'})}
                  className="flex-1 px-4 py-3 rounded-lg font-medium transition-all"
                  style={{
                    backgroundColor: settings.language === 'hindi' ? THEME.accent : THEME.cardBorder,
                    color: settings.language === 'hindi' ? '#000000' : THEME.textSecondary
                  }}
                >
                  Hindi
                </button>
              </div>
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full py-3 font-semibold"
              style={{ backgroundColor: THEME.accent, color: '#000000' }}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Make Test Call Card */}
        <div 
          className="rounded-xl p-6"
          style={{ backgroundColor: THEME.cardBg, border: `1px solid ${THEME.cardBorder}` }}
        >
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: THEME.textPrimary }}>
            <PhoneCall className="w-5 h-5" style={{ color: THEME.accent }} />
            Make Test Call
          </h3>
          
          <p className="mb-4" style={{ color: THEME.textSecondary }}>
            Test your AI assistant by making a call to any phone number. The AI will use your current settings and script.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.textSecondary }}>
                Phone Number
              </label>
              <input
                type="text"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="+91..."
                className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2"
                style={{ 
                  backgroundColor: THEME.inputBg, 
                  border: `1px solid ${THEME.cardBorder}`,
                  color: THEME.textPrimary
                }}
              />
            </div>

            <Button
              onClick={handleTestCall}
              disabled={testing || !testPhone}
              className="w-full py-4 font-semibold text-lg"
              style={{ backgroundColor: THEME.accent, color: '#000000' }}
            >
              {testing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Calling...
                </>
              ) : (
                <>
                  <Phone className="w-5 h-5 mr-2" />
                  Make Test Call
                </>
              )}
            </Button>

            <p className="text-xs text-center" style={{ color: THEME.textSecondary }}>
              Each test call uses credits from your Voice AI balance
            </p>
          </div>
        </div>
      </div>

      {/* Call Script Preview */}
      {callScript && (
        <div 
          className="rounded-xl p-6"
          style={{ backgroundColor: THEME.cardBg, border: `1px solid ${THEME.cardBorder}` }}
        >
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: THEME.textPrimary }}>
            <FileText className="w-5 h-5" style={{ color: THEME.accent }} />
            Current Call Script Preview
          </h3>
          <div 
            className="rounded-lg p-4"
            style={{ backgroundColor: THEME.inputBg, border: `1px solid ${THEME.cardBorder}` }}
          >
            <p className="font-medium mb-2" style={{ color: THEME.accent }}>{callScript.name}</p>
            <p className="text-sm" style={{ color: THEME.textSecondary }}>
              {callScript.greeting || "Hello! This is your AI assistant. How can I help you today?"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceAISettings;
